import { hasPaidPlan, hasProductAccess, isFreeTier, waitForSubscriptionActivation } from '@/lib/subscription-access'
import { useFreePlan } from '@/hooks/use-free-plan'
import { useBillingPortal } from '@/hooks/use-billing-portal'
import { ProFeatureNotice } from '@/components/pro-feature-notice'
import { PRO_ADDS } from '@/lib/free-plan'
import { useState, useEffect } from 'react'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { PageHeader } from '@/components/app/page-header'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog'
import { useAuth } from '@/hooks/use-auth'
import { AccountEmailsCard } from '@/components/account-emails-card'
import { PasswordCard } from '@/components/password-card'
import { annualSavingPercent, MAX_ANNUAL_SAVING_PERCENT, tiers, type BillingInterval } from '@/lib/plans'
import type { CreateApiKeyInput, OAuthClientWithSecret } from '@/lib/api'
import { apiV2, ApiV2Error, type V2ApiKey, type V2AuditLog } from '@/lib/api-v2-client'
import { billingProvider } from '@/lib/billing-provider'
import { startCheckout } from '@/lib/checkout'
import { grantDate, sponsoredUntil, TEAM_OVER_GRANT } from '@/lib/nonprofit'
import { NonprofitCard } from '@/components/nonprofit-card'
import { RedeemCodeCard } from '@/components/redeem-code-card'
import { toast } from 'sonner'

export const Route = createFileRoute('/_authenticated/dashboard/settings/')({
  component: SettingsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    billing: (search.billing as string) || undefined,
    plan: (search.plan as string) || undefined,
    tab: (search.tab as string) || undefined,
  }),
})

function SettingsPage() {
  const { user, organization, refreshAuth } = useAuth()
  const queryClient = useQueryClient()
  const { billing, plan, tab } = Route.useSearch()
  const navigate = useNavigate()
  const isAdmin = user?.role === 'owner' || user?.role === 'admin'

  // Paddle confirms the payment before its webhook updates our organization.
  // Refresh auth until the server enables the paid features so the gate opens
  // without requiring the customer to reload the page manually.
  useEffect(() => {
    if (billing !== 'success' || !plan) return

    const controller = new AbortController()
    toast.success('Payment received. Activating your subscription…')
    void waitForSubscriptionActivation(refreshAuth, { signal: controller.signal }).then(async (active) => {
      if (controller.signal.aborted) return
      await queryClient.invalidateQueries({ queryKey: ['billing-subscription'] })
      if (active) {
        toast.success(`${plan.charAt(0).toUpperCase() + plan.slice(1)} subscription activated.`)
      } else {
        toast.info('Activation is taking longer than expected. This page will update when you refresh.')
      }
      // Drop billing/plan so a later reload does not replay the payment toasts.
      navigate({ to: '.', search: { billing: undefined, plan: undefined, tab: 'billing' }, replace: true })
    })

    return () => controller.abort()
  }, [billing, plan, queryClient, refreshAuth, navigate])

  const productAccess = hasProductAccess(organization)
  // The free plan has no API keys or OAuth clients (the server answers 402 pro_required).
  const freeTier = isFreeTier(organization)
  const defaultTab = tab === 'billing' || billing === 'success' ? 'billing' : 'profile'

  return (
    <div className="space-y-6">
      <PageHeader title="Settings" description="Manage your account and preferences" />

      <Tabs defaultValue={defaultTab}>
        <TabsList className="max-w-full justify-start overflow-x-auto">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="security">Security</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
          <TabsTrigger disabled={!productAccess} value="api-keys">API Keys</TabsTrigger>
          <TabsTrigger disabled={!productAccess} value="integrations">Integrations</TabsTrigger>
          <TabsTrigger disabled={!productAccess} value="audit-log">Audit Log</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-6">
          <ProfileTab />
        </TabsContent>

        <TabsContent value="security" className="mt-6">
          <SecurityTab />
        </TabsContent>

        <TabsContent value="billing" className="mt-6">
          {isAdmin ? (
            <BillingTab />
          ) : (
            <div className="max-w-2xl space-y-4">
              <Card>
                <CardContent className="py-10 text-center text-muted-foreground">
                  You need admin or owner permissions to manage billing.
                </CardContent>
              </Card>
              {/* Members see the nonprofit state; only an owner or admin claims or applies. */}
              <NonprofitCard />
            </div>
          )}
        </TabsContent>

        <TabsContent value="api-keys" className="mt-6">
          {freeTier ? (
            <ProFeatureNotice title="API keys are on Pro">
              API keys, the CLI and the MCP connector need a paid plan. The free plan is for the dashboard.
            </ProFeatureNotice>
          ) : isAdmin ? (
            <ApiKeysTab />
          ) : (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                You need admin or owner permissions to manage API keys.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="integrations" className="mt-6">
          {freeTier ? (
            <ProFeatureNotice title="OAuth clients are on Pro">
              OAuth clients let tools such as Claude reach your data over MCP. They need a paid plan.
            </ProFeatureNotice>
          ) : isAdmin ? (
            <IntegrationsTab />
          ) : (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                You need admin or owner permissions to manage integrations.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="audit-log" className="mt-6">
          {freeTier || organization?.plan === 'free' ? (
            <Card>
              <CardContent className="py-10 text-center">
                <p className="text-muted-foreground mb-4">
                  Audit logs are available on Pro and Team plans.
                </p>
                <Button asChild>
                  <a href="/pricing">Upgrade to Pro</a>
                </Button>
              </CardContent>
            </Card>
          ) : isAdmin ? (
            <AuditLogTab />
          ) : (
            <Card>
              <CardContent className="py-10 text-center text-muted-foreground">
                You need admin or owner permissions to view audit logs.
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Profile Tab (extracted from original settings page)
// ---------------------------------------------------------------------------

function ProfileTab() {
  const { user, organization, logout } = useAuth()
  const navigate = useNavigate()

  const [name, setName] = useState(user?.name ?? '')

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deletePassword, setDeletePassword] = useState('')

  const isOAuthUser = !!user?.provider

  const saveProfileMutation = useMutation({
    mutationFn: () => apiV2.updateProfile({ name }),
    onSuccess: () => toast.success('Profile updated'),
    onError: (err) => toast.error(err instanceof ApiV2Error ? err.message : 'Failed to update profile'),
  })

  const deleteAccountMutation = useMutation({
    mutationFn: () => apiV2.deleteAccount({ password: deletePassword || undefined }),
    onSuccess: async (data) => {
      await logout()
      if (data?.goodbye_token) {
        navigate({ to: '/goodbye', search: { token: data.goodbye_token } })
      } else {
        navigate({ to: '/' })
      }
    },
    onError: (err) => toast.error(err instanceof ApiV2Error ? err.message : 'Failed to delete account'),
  })

  function handleSaveProfile(e: React.FormEvent) {
    e.preventDefault()
    saveProfileMutation.mutate()
  }

  function handleDeleteAccount(e: React.FormEvent) {
    e.preventDefault()
    deleteAccountMutation.mutate()
  }

  return (
    <div className="grid gap-6">
      {/* Profile Card */}
      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
          <CardDescription>Your personal account information</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Name</Label>
              <Input id="name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Account Type</Label>
              <Input
                aria-label="Account Type"
                value={
                  user?.provider
                    ? `${user.provider.charAt(0).toUpperCase()}${user.provider.slice(1)}`
                    : 'Email'
                }
                disabled
              />
            </div>
            {organization && (
              <div className="space-y-2">
                <Label>Organization</Label>
                <Input aria-label="Organization" value={organization.name} disabled />
              </div>
            )}
            <div className="flex justify-end">
              <Button type="submit" disabled={saveProfileMutation.isPending}>
                {saveProfileMutation.isPending ? 'Saving...' : 'Save Profile'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <AccountEmailsCard />

      {/* Password card (only for email users) */}
      {!isOAuthUser && <PasswordCard />}

      {/* Danger Zone */}
      <Card>
        <CardHeader>
          <CardTitle>Danger Zone</CardTitle>
          <CardDescription>Irreversible account actions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between rounded-lg border border-status-down/30 p-4 dark:border-status-down/30">
            <div>
              <p className="font-medium">Delete Account</p>
              <p className="text-sm text-muted-foreground">
                Permanently delete your account and all data
              </p>
            </div>
            <Button variant="destructive" onClick={() => setShowDeleteDialog(true)}>
              Delete Account
            </Button>
          </div>

          {showDeleteDialog && (
            <div className="mt-4 rounded-lg border border-status-down/30 p-4 dark:border-status-down/30">
              <p className="mb-4 font-medium text-status-down">
                Are you sure? This will permanently delete your account, organization,
                and all associated data. This cannot be undone.
              </p>
              <form onSubmit={handleDeleteAccount} className="space-y-4">
                {!isOAuthUser && (
                  <div className="space-y-2">
                    <Label htmlFor="delete-password">Enter your password to confirm</Label>
                    {/* Not required: an account created with an email link has no password, and the
                        API accepts the delete without one (it refuses a wrong password). */}
                    <Input
                      id="delete-password"
                      type="password"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      autoComplete="current-password"
                    />
                    <p className="text-sm text-muted-foreground">
                      Signed up with an email link? You have no password. Leave this empty.
                    </p>
                  </div>
                )}
                <div className="flex gap-2 justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setShowDeleteDialog(false)
                      setDeletePassword('')
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" variant="destructive" disabled={deleteAccountMutation.isPending}>
                    {deleteAccountMutation.isPending ? 'Deleting...' : 'Delete My Account'}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

// ---------------------------------------------------------------------------
// API Keys Tab
// ---------------------------------------------------------------------------

function ApiKeysTab() {
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['api-keys'],
    queryFn: () => apiV2.listApiKeys(),
  })

  const apiKeys = data?.data ?? []

  const revokeMutation = useMutation({
    mutationFn: (id: string) => apiV2.revokeApiKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] })
      toast.success('API key revoked')
    },
    onError: (err) =>
      toast.error(err instanceof ApiV2Error ? err.message : 'Failed to revoke key'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiV2.deleteApiKey(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] })
      toast.success('API key deleted')
    },
    onError: (err) =>
      toast.error(err instanceof ApiV2Error ? err.message : 'Failed to delete key'),
  })

  const [revokeTarget, setRevokeTarget] = useState<V2ApiKey | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<V2ApiKey | null>(null)

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-10">
          <div className="space-y-3">
            <div className="h-8 animate-pulse rounded bg-muted" />
            <div className="h-8 animate-pulse rounded bg-muted" />
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">API Keys</h2>
          <p className="text-sm text-muted-foreground">
            Manage keys for programmatic access to the Uptrack API
          </p>
        </div>
        <CreateApiKeyDialog />
      </div>

      {apiKeys.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-muted-foreground mb-4">
              No API keys yet. Create one to start using the Uptrack API.
            </p>
            <CreateApiKeyDialog />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Key</TableHead>
                <TableHead>Scopes</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Used</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {apiKeys.map((key) => (
                <TableRow key={key.id}>
                  <TableCell className="font-medium">{key.name}</TableCell>
                  <TableCell>
                    <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                      {key.key_prefix}...
                    </code>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {key.scopes.map((scope) => (
                        <Badge key={scope} variant="secondary" className="text-xs">
                          {scope}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <KeyStatusBadge apiKey={key} />
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {key.last_used_at
                      ? new Date(key.last_used_at).toLocaleDateString()
                      : 'Never'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {key.expires_at
                      ? new Date(key.expires_at).toLocaleDateString()
                      : 'Never'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex gap-2 justify-end">
                      {key.is_active && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setRevokeTarget(key)}
                        >
                          Revoke
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-status-down hover:text-status-down"
                        onClick={() => setDeleteTarget(key)}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Revoke Confirmation Dialog */}
      <Dialog open={!!revokeTarget} onOpenChange={() => setRevokeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke API Key</DialogTitle>
            <DialogDescription>
              Revoke key "{revokeTarget?.name}"? Any integrations using this key
              will stop working immediately.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={revokeMutation.isPending}
              onClick={() => {
                if (revokeTarget) {
                  revokeMutation.mutate(revokeTarget.id, {
                    onSuccess: () => setRevokeTarget(null),
                  })
                }
              }}
            >
              {revokeMutation.isPending ? 'Revoking...' : 'Revoke Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete API Key</DialogTitle>
            <DialogDescription>
              Delete key "{deleteTarget?.name}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (deleteTarget) {
                  deleteMutation.mutate(deleteTarget.id, {
                    onSuccess: () => setDeleteTarget(null),
                  })
                }
              }}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Key'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Create API Key Dialog
// ---------------------------------------------------------------------------

const EXPIRATION_OPTIONS = [
  { label: 'Never', value: '' },
  { label: '30 days', days: 30 },
  { label: '60 days', days: 60 },
  { label: '90 days', days: 90 },
  { label: '1 year', days: 365 },
] as const

function CreateApiKeyDialog() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<string[]>(['read', 'write'])
  const [expirationDays, setExpirationDays] = useState('')
  const [rawKey, setRawKey] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  const createMutation = useMutation({
    mutationFn: (data: CreateApiKeyInput) => apiV2.createApiKey(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['api-keys'] })
      setRawKey(result.raw_key)
    },
    onError: (err) =>
      toast.error(err instanceof ApiV2Error ? err.message : 'Failed to create key'),
  })

  function handleCreate() {
    const expiresAt = expirationDays
      ? new Date(Date.now() + Number(expirationDays) * 86400000).toISOString()
      : null

    createMutation.mutate({
      name: name || 'Untitled Key',
      scopes,
      expires_at: expiresAt,
    })
  }

  function handleCopy() {
    if (rawKey) {
      navigator.clipboard.writeText(rawKey)
      setCopied(true)
      toast.success('Key copied to clipboard')
      setTimeout(() => setCopied(false), 2000)
    }
  }

  function handleClose() {
    setOpen(false)
    setName('')
    setScopes(['read', 'write'])
    setExpirationDays('')
    setRawKey(null)
    setCopied(false)
    createMutation.reset()
  }

  function toggleScope(scope: string) {
    setScopes((prev) =>
      prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]
    )
  }

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : handleClose())}>
      <DialogTrigger asChild>
        <Button>Create API Key</Button>
      </DialogTrigger>
      <DialogContent>
        {rawKey ? (
          <>
            <DialogHeader>
              <DialogTitle>API Key Created</DialogTitle>
              <DialogDescription>
                Store this key securely. It will not be shown again.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="rounded-lg border bg-muted p-3">
                <code className="text-sm break-all select-all">{rawKey}</code>
              </div>
              <Button onClick={handleCopy} className="w-full">
                {copied ? 'Copied!' : 'Copy to Clipboard'}
              </Button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Create API Key</DialogTitle>
              <DialogDescription>
                Create a new key for programmatic access to the Uptrack API.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="key-name">Name</Label>
                <Input
                  id="key-name"
                  placeholder="e.g., CI Pipeline"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Scopes</Label>
                <div className="flex gap-2">
                  {['read', 'write', 'admin'].map((scope) => (
                    <Button
                      key={scope}
                      type="button"
                      size="sm"
                      variant={scopes.includes(scope) ? 'default' : 'outline'}
                      onClick={() => toggleScope(scope)}
                    >
                      {scope}
                    </Button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Select which permissions this key should have
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="key-expiration">Expiration</Label>
                <select
                  id="key-expiration"
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs"
                  value={expirationDays}
                  onChange={(e) => setExpirationDays(e.target.value)}
                >
                  {EXPIRATION_OPTIONS.map((opt) => (
                    <option key={opt.label} value={'days' in opt ? String(opt.days) : ''}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={createMutation.isPending || scopes.length === 0}
              >
                {createMutation.isPending ? 'Creating...' : 'Create Key'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Billing Tab
// ---------------------------------------------------------------------------

const PLAN_LABELS: Record<string, string> = {
  free: 'Free',
  pro: 'Pro',
  team: 'Team',
  business: 'Business',
}

// Annual subscriptions have a billing period of about a year; monthly about 30 days.
function subscriptionInterval(start?: string | null, end?: string | null): BillingInterval {
  if (!start || !end) return 'monthly'
  const days = (new Date(end).getTime() - new Date(start).getTime()) / 86_400_000
  return days > 60 ? 'annual' : 'monthly'
}

// "1,000 monitors — all at 30s" → "1,000"
function monitorLimit(plan: string): string {
  const first = tiers.find((t) => t.plan === plan)?.features[0]?.name ?? ''
  return first.match(/^([\d,]+) monitors/)?.[1] ?? ''
}

function planPrice(plan: string, interval: BillingInterval): string {
  const tier = tiers.find((t) => t.plan === plan)
  if (!tier) return ''
  return interval === 'annual' ? `$${tier.annualTotalPrice}/yr` : `$${tier.monthlyPrice}/mo`
}

const TIER_RANK: Record<string, number> = { free: 0, pro: 1, team: 2, business: 3 }
const tierRank = (plan: string) => TIER_RANK[plan] ?? 0

function BillingTab() {
  const { organization, refreshAuth } = useAuth()
  const queryClient = useQueryClient()
  const currentPlan = organization?.plan ?? 'free'
  const freeTier = isFreeTier(organization)
  const freePlan = useFreePlan()
  const { open: handleManageBilling, loading: loadingPortal } = useBillingPortal()
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [pendingPlanChange, setPendingPlanChange] = useState<string | null>(null)
  const [openingCheckout, setOpeningCheckout] = useState(false)

  const { data: billingData, isLoading } = useQuery({
    queryKey: ['billing-subscription'],
    queryFn: () => apiV2.getSubscription(),
  })

  const cancelMutation = useMutation({
    mutationFn: () => apiV2.cancelSubscription(),
    onSuccess: async (result) => {
      const ends = result.access_ends_at ?? billingData?.data?.current_period_end
      toast.success(ends
        ? `Subscription cancelled. You keep access until ${new Date(ends).toLocaleDateString()}.`
        : 'Subscription cancelled. You keep access until the end of the billing period.')
      setShowCancelDialog(false)
      await queryClient.invalidateQueries({ queryKey: ['billing-subscription'] })
    },
    onError: (err) =>
      toast.error(err instanceof ApiV2Error ? err.message : 'Failed to cancel subscription'),
  })

  const changePlanMutation = useMutation({
    mutationFn: (plan: string) => apiV2.changePlan(plan, interval),
    onSuccess: async (_, plan) => {
      setPendingPlanChange(null)
      toast.success(`Changing plan to ${PLAN_LABELS[plan] ?? plan}…`)
      // Paddle applies the change first; our plan follows its webhook. Wait for
      // it so the page does not show the old plan and a second change button.
      let applied = false
      for (let i = 0; i < 20 && !applied; i += 1) {
        const org = await refreshAuth().catch(() => null)
        applied = org?.plan === plan
        if (!applied) await new Promise((r) => setTimeout(r, 1500))
      }
      await queryClient.invalidateQueries({ queryKey: ['billing-subscription'] })
      if (applied) toast.success(`Plan changed to ${PLAN_LABELS[plan] ?? plan}.`)
      else toast.info('The plan change is taking longer than expected. Refresh this page in a minute.')
    },
    onError: (err) => {
      toast.error(err instanceof ApiV2Error ? err.message : 'Failed to change plan')
      setPendingPlanChange(null)
    },
  })

  function handleChangePlan(plan: string) {
    setPendingPlanChange(plan)
  }

  function confirmChangePlan() {
    if (pendingPlanChange) {
      changePlanMutation.mutate(pendingPlanChange)
    }
  }

  // A grant-only organization has no subscription to change, so Team goes through checkout (D13).
  async function handleUpgradeToTeam() {
    setOpeningCheckout(true)
    try {
      await startCheckout('team', 'monthly', { onClose: () => setOpeningCheckout(false) })
    } catch (err) {
      toast.error(err instanceof ApiV2Error ? err.message : 'Failed to start checkout')
      setOpeningCheckout(false)
    }
  }

  const subscription = billingData?.data
  const interval = subscriptionInterval(subscription?.current_period_start, subscription?.current_period_end)
  const cancelScheduled = !!subscription?.scheduled_cancel_at
  const managedByApple = billingProvider(subscription) === 'apple'
  const liveStatus = subscription?.status === 'active' || subscription?.status === 'trialing' || subscription?.status === 'past_due'
  const otherPlans = tiers.filter((t) => t.plan !== currentPlan)
  // A live nonprofit grant (OpenSpec 136). Grant only: the plan comes from it, not from a subscription.
  const grant = billingData?.grant ?? null
  // The plan comes from the nonprofit grant alone: no live Paddle or App Store subscription.
  const planFromGrant = !!grant && !liveStatus && !managedByApple
  const grantOnly = planFromGrant && tierRank(currentPlan) < tierRank('team')

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-10">
          <div className="h-8 animate-pulse rounded bg-muted" />
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="max-w-2xl space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Billing</h2>
        <p className="text-sm text-muted-foreground">
          Manage your subscription and plan
        </p>
      </div>

      {/* Current Plan */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            Current Plan
            <Badge variant={currentPlan === 'free' ? 'secondary' : 'default'}>
              {PLAN_LABELS[currentPlan] ?? currentPlan}
            </Badge>
          </CardTitle>
          <CardDescription>
            {freeTier
              ? `You're on the free plan: ${freePlan.monitors} monitors, checked every ${freePlan.intervalSeconds} seconds from ${freePlan.location}.`
              : currentPlan === 'free'
                ? "Choose a subscription to enable monitoring and alerts."
                : `You're on the ${PLAN_LABELS[currentPlan]} plan.`}
          </CardDescription>
          {grant && (
            <p className="text-sm text-body" data-testid="sponsored-line">
              {sponsoredUntil(grant.ends_at)}
            </p>
          )}
        </CardHeader>
        {subscription && (
          <CardContent>
            <div className="grid gap-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <Badge variant={subscription.status === 'active' || subscription.status === 'trialing' ? 'default' : 'destructive'}>
                  {subscription.status === 'trialing' ? 'Trial' : subscription.status}
                </Badge>
              </div>
              {cancelScheduled ? (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Access ends</span>
                  <span>{new Date(subscription.scheduled_cancel_at!).toLocaleDateString()}</span>
                </div>
              ) : subscription.current_period_end && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    {subscription.status === 'trialing' ? 'Trial ends' : 'Next billing date'}
                  </span>
                  <span>{new Date(subscription.current_period_end).toLocaleDateString()}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Billing</span>
                <span>{interval === 'annual' ? 'Annual' : 'Monthly'}</span>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Upgrade options (shown for free users) */}
      {currentPlan === 'free' && (
        <Card>
          <CardHeader>
            <CardTitle>Upgrade Your Plan</CardTitle>
            <CardDescription>
              {freeTier
                ? `Pro adds ${PRO_ADDS}. Save ${annualSavingPercent('pro')}% on Pro with annual billing.`
                : `Get faster checks, more monitors, and advanced features. Save up to ${MAX_ANNUAL_SAVING_PERCENT}% with annual billing.`}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/pricing">View Plans & Pricing</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Pro through a nonprofit grant, with no subscription: Team is still for sale (D13) */}
      {grantOnly && (
        <Card>
          <CardHeader>
            <CardTitle>Need more than Pro?</CardTitle>
            <CardDescription>{TEAM_OVER_GRANT}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" disabled={openingCheckout} onClick={handleUpgradeToTeam}>
              {openingCheckout ? 'Opening...' : `Upgrade to Team (${planPrice('team', 'monthly')})`}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Bought in the iOS app: the App Store manages it, so no Paddle actions here */}
      {managedByApple && (
        <Card>
          <CardHeader>
            <CardTitle>Managed by the App Store</CardTitle>
            <CardDescription>
              This plan was bought in the Uptrack iOS app. To change or cancel it, open Settings on your iPhone or
              iPad, tap your name, then tap Subscriptions.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {/* Change plan / Cancel (shown for paid users) */}
      {currentPlan !== 'free' && liveStatus && !managedByApple && (
        <Card>
          <CardHeader>
            <CardTitle>Manage Subscription</CardTitle>
            {cancelScheduled && (
              <CardDescription>
                Cancellation scheduled. To keep your plan, resume it in Manage Billing.
              </CardDescription>
            )}
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {!cancelScheduled && otherPlans.map((tier) => (
                <Button
                  key={tier.plan}
                  variant="outline"
                  disabled={changePlanMutation.isPending}
                  onClick={() => handleChangePlan(tier.plan)}
                >
                  {changePlanMutation.isPending && pendingPlanChange === tier.plan
                    ? 'Changing...'
                    : `${tierRank(tier.plan) > tierRank(currentPlan) ? 'Upgrade' : 'Downgrade'} to ${tier.name} (${planPrice(tier.plan, interval)})`}
                </Button>
              ))}

              <Button
                variant="outline"
                disabled={loadingPortal}
                onClick={handleManageBilling}
              >
                {loadingPortal ? 'Opening...' : 'Manage Billing'}
              </Button>

              {!cancelScheduled && (
                <Button
                  variant="destructive"
                  onClick={() => setShowCancelDialog(true)}
                >
                  Cancel Subscription
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Plan change confirmation dialog */}
      <Dialog open={!!pendingPlanChange} onOpenChange={(open) => { if (!open) setPendingPlanChange(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pendingPlanChange ? `Change to ${PLAN_LABELS[pendingPlanChange] ?? pendingPlanChange}` : 'Change plan'}
            </DialogTitle>
            <DialogDescription>
              {pendingPlanChange && (
                <>
                  Switch to {PLAN_LABELS[pendingPlanChange]} ({planPrice(pendingPlanChange, interval)}), billed {interval === 'annual' ? 'annually' : 'monthly'}.
                  {' '}Paddle charges or credits the prorated difference now.
                  {tierRank(pendingPlanChange) < tierRank(currentPlan) && (
                    <> Monitors above the {PLAN_LABELS[pendingPlanChange]} limit ({monitorLimit(pendingPlanChange)}) will be paused.</>
                  )}
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingPlanChange(null)}>
              Cancel
            </Button>
            <Button
              disabled={changePlanMutation.isPending}
              onClick={confirmChangePlan}
            >
              {changePlanMutation.isPending ? 'Changing...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel confirmation dialog */}
      <CancelDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        onConfirm={() => cancelMutation.mutate()}
        isPending={cancelMutation.isPending}
        accessEndsAt={subscription?.current_period_end}
        // The grant text is true only when the grant outlives the paid period.
        grantEndsAt={grant && (!subscription?.current_period_end || new Date(grant.ends_at) > new Date(subscription.current_period_end)) ? grant.ends_at : null}
      />

      {/* Free Pro for nonprofits (OpenSpec 136); hidden while the program is off */}
      <NonprofitCard />

      {/* Redeem a code (promo codes); hidden while the feature is off */}
      <RedeemCodeCard />

      {/* Add-ons */}
      {currentPlan !== 'free' && !planFromGrant && <AddOnsSection />}
    </div>
  )
}

function AddOnsSection() {
  const queryClient = useQueryClient()

  const { data } = useQuery({
    queryKey: ['add-ons'],
    queryFn: () => apiV2.getAddOns(),
  })

  const labels: Record<string, { name: string; unit: string }> = {
    extra_monitors: { name: 'Extra Monitors', unit: '/monitor/mo' },
    extra_fast_slots: { name: 'Extra Fast Slots (30s)', unit: '/slot/mo' },
    extra_teammates: { name: 'Extra Team Members', unit: '/user/mo' },
    extra_sms: { name: 'Extra SMS/Call Alerts', unit: '/alert' },
    extra_subscribers: { name: 'Extra Subscribers', unit: '/subscriber/mo' },
  }

  async function handleChange(type: string, quantity: number) {
    try {
      await apiV2.setAddOn(type, Math.max(0, quantity))
      queryClient.invalidateQueries({ queryKey: ['add-ons'] })
    } catch {
      toast.error('Failed to update add-on')
    }
  }

  if (!data) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add-Ons</CardTitle>
        <CardDescription>
          Need more than your plan includes? Add extras without upgrading.
          {data.monthly_cost_cents > 0 && (
            <span className="ml-2 font-medium text-foreground">
              Current add-on cost: ${(data.monthly_cost_cents / 100).toFixed(2)}/mo
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {data.available_types.map((available) => {
            const current = data.data.find((a) => a.type === available.type)
            const qty = current?.quantity ?? 0
            const label = labels[available.type] || { name: available.type, unit: '/mo' }

            return (
              <div key={available.type} className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">{label.name}</p>
                  <p className="text-xs text-muted-foreground">
                    ${(available.unit_price_cents / 100).toFixed(2)}{label.unit}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={qty === 0}
                    onClick={() => handleChange(available.type, qty - 1)}
                  >
                    −
                  </Button>
                  <span className="w-8 text-center text-sm font-medium">{qty}</span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleChange(available.type, qty + 1)}
                  >
                    +
                  </Button>
                </div>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Cancel Dialog with downgrade preview
// ---------------------------------------------------------------------------

function CancelDialog({
  open,
  onOpenChange,
  onConfirm,
  isPending,
  accessEndsAt,
  grantEndsAt,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  isPending: boolean
  accessEndsAt?: string | null
  /** End of a live nonprofit grant: Pro continues through it after the paid plan ends. */
  grantEndsAt?: string | null
}) {

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Cancel Subscription</DialogTitle>
          <DialogDescription>
            You keep full access until {accessEndsAt ? new Date(accessEndsAt).toLocaleDateString() : 'the end of the billing period'}.
            {grantEndsAt
              ? `After that, your nonprofit grant keeps Pro running until ${grantDate(grantEndsAt)}, so monitoring and alerts continue.`
              : 'After that, monitoring and alerts stop. Your monitors, history and settings are kept, so you can subscribe again anytime.'}
          </DialogDescription>
        </DialogHeader>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Keep Subscription
          </Button>
          <Button
            variant="destructive"
            disabled={isPending}
            onClick={onConfirm}
          >
            {isPending ? 'Cancelling...' : 'Cancel Subscription'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Audit Log Tab
// ---------------------------------------------------------------------------

const ACTION_CATEGORIES = [
  { label: 'All Actions', value: '' },
  { label: 'Team', value: 'team.' },
  { label: 'Monitor', value: 'monitor.' },
  { label: 'Alert Channel', value: 'alert_channel.' },
  { label: 'Status Page', value: 'status_page.' },
  { label: 'Incident', value: 'incident.' },
  { label: 'User', value: 'user.' },
  { label: 'Organization', value: 'organization.' },
]

const PAGE_SIZE = 50

const DATE_RANGES = [
  { label: 'All time', value: '' },
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' },
]

function sinceFromDays(days: string): string | undefined {
  if (!days) return undefined
  const d = new Date()
  d.setDate(d.getDate() - Number(days))
  return d.toISOString()
}

function AuditLogTab() {
  const { organization } = useAuth()
  const [actionFilter, setActionFilter] = useState('')
  const [userFilter, setUserFilter] = useState('')
  const [dateRange, setDateRange] = useState('')
  const [offset, setOffset] = useState(0)

  const { data: members } = useQuery({
    queryKey: ['team-members', organization?.id],
    queryFn: () => apiV2.listMembers(),
    enabled: !!organization?.id,
  })

  const { data, isLoading } = useQuery({
    queryKey: ['audit-logs', organization?.id, actionFilter, userFilter, dateRange, offset],
    queryFn: () =>
      apiV2.listAuditLogs({
        limit: PAGE_SIZE,
        offset,
        action: actionFilter || undefined,
        user_id: userFilter || undefined,
        since: sinceFromDays(dateRange),
      }),
    enabled: !!organization?.id,
  })

  const logs = data?.data ?? []
  const total = data?.meta?.total ?? 0

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-10">
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-10 animate-pulse rounded bg-muted" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold">Audit Log</h2>
          <p className="text-sm text-muted-foreground">
            Activity history for your organization ({total} entries)
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            className="flex h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs"
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value)
              setOffset(0)
            }}
          >
            {ACTION_CATEGORIES.map((cat) => (
              <option key={cat.label} value={cat.value}>
                {cat.label}
              </option>
            ))}
          </select>
          {members?.data && members.data.length > 1 && (
            <select
              className="flex h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs"
              value={userFilter}
              onChange={(e) => {
                setUserFilter(e.target.value)
                setOffset(0)
              }}
            >
              <option value="">All users</option>
              {members.data.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          )}
          <select
            className="flex h-9 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs"
            value={dateRange}
            onChange={(e) => {
              setDateRange(e.target.value)
              setOffset(0)
            }}
          >
            {DATE_RANGES.map((r) => (
              <option key={r.label} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {logs.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            {actionFilter || userFilter || dateRange ? 'No entries match these filters.' : 'No audit log entries yet.'}
          </CardContent>
        </Card>
      ) : (
        <>
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Actor</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>When</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      {log.user ? (
                        <div>
                          <p className="font-medium text-sm">{log.user.name}</p>
                          <p className="text-xs text-muted-foreground">{log.user.email}</p>
                        </div>
                      ) : (
                        <span className="text-sm text-muted-foreground">System</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs font-mono">
                        {formatAction(log.action ?? 'unknown')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      <AuditLogDetails log={log} />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {log.created_at ? formatRelativeTime(log.created_at) : '—'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          {offset + PAGE_SIZE < total && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
              >
                Load More ({total - offset - PAGE_SIZE} remaining)
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

function AuditLogDetails({ log }: { log: V2AuditLog }) {
  const parts: string[] = []

  if (log.resource_type) {
    parts.push(log.resource_type)
  }

  if (log.metadata) {
    // metadata is free-form jsonb (typed {} by the schema); narrow to string fields.
    const meta = log.metadata as Record<string, string | undefined>
    if (meta.email) parts.push(meta.email)
    if (meta.name) parts.push(`"${meta.name}"`)
    if (meta.member_email) parts.push(meta.member_email)
    if (meta.old_role && meta.new_role) parts.push(`${meta.old_role} → ${meta.new_role}`)
    if (meta.role && !meta.old_role) parts.push(`role: ${meta.role}`)
    if (meta.type) parts.push(meta.type)
    if (meta.monitor_type) parts.push(meta.monitor_type)
    if (meta.slug) parts.push(meta.slug)
    if (meta.cause) parts.push(meta.cause)
  }

  return <span className="text-muted-foreground">{parts.join(' · ') || '—'}</span>
}

function formatAction(action: string): string {
  return action.replace(/[._]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)

  if (diffMin < 1) return 'Just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHrs = Math.floor(diffMin / 60)
  if (diffHrs < 24) return `${diffHrs}h ago`
  const diffDays = Math.floor(diffHrs / 24)
  if (diffDays < 30) return `${diffDays}d ago`
  return date.toLocaleDateString()
}

// ---------------------------------------------------------------------------
// Security Tab — Two-Factor Authentication
// ---------------------------------------------------------------------------

function SecurityTab() {
  const { organization } = useAuth()
  return (
    <div className="space-y-6">
      <TwoFactorCard />
      <SessionsCard />
      {/* SSO and a custom sender are Business features: not shown on the free plan. */}
      {hasPaidPlan(organization) && <><SsoCard /><CustomSenderCard /></>}
    </div>
  )
}

// Sessions last 30 days. "Sign out of all devices" ends every other browser
// session; this one stays signed in (the server re-mints its cookie).
function SessionsCard() {
  const revoke = useMutation({
    mutationFn: () => apiV2.revokeAllSessions(),
    onSuccess: () => toast.success('Signed out of all other devices.'),
    onError: (err) => toast.error(err instanceof ApiV2Error ? err.message : 'Failed to sign out other devices'),
  })
  return (
    <Card>
      <CardHeader>
        <CardTitle>Sessions</CardTitle>
        <CardDescription>
          You stay signed in for up to 30 days on each device. Changing your password or two-factor settings also signs out your other devices.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button variant="outline" disabled={revoke.isPending} onClick={() => revoke.mutate()}>
          {revoke.isPending ? 'Signing out...' : 'Sign out of all other devices'}
        </Button>
      </CardContent>
    </Card>
  )
}

function CustomSenderCard() {
  const [senderName, setSenderName] = useState('')
  const [senderEmail, setSenderEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { data, refetch } = useQuery({
    queryKey: ['custom-sender'],
    queryFn: () => apiV2.getCustomSender(),
  })

  const sender = data?.data

  async function handleSetup(e: React.FormEvent) {
    e.preventDefault()
    if (!senderName.trim() || !senderEmail.trim()) return
    setIsSubmitting(true)
    try {
      await apiV2.setupCustomSender(senderName.trim(), senderEmail.trim())
      toast.success('Verification email sent! Check your inbox.')
      setSenderName('')
      setSenderEmail('')
      refetch()
    } catch {
      toast.error('Failed to set up custom sender. Business plan required.')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleDelete() {
    try {
      await apiV2.deleteCustomSender()
      refetch()
      toast.success('Custom sender removed')
    } catch {
      toast.error('Failed to remove sender')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Custom Email Sender <span className="text-xs font-normal text-muted-foreground">(Business)</span></CardTitle>
        <CardDescription>Send alert emails from your own domain instead of alerts@uptrack.app.</CardDescription>
      </CardHeader>
      <CardContent>
        {sender ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className={`h-2 w-2 rounded-full ${sender.verified ? 'bg-status-up' : 'bg-status-degraded'}`} />
              <span className="text-sm font-medium">{sender.sender_name} &lt;{sender.sender_email}&gt;</span>
              <Badge variant={sender.verified ? 'default' : 'secondary'}>
                {sender.verified ? 'Verified' : 'Pending verification'}
              </Badge>
            </div>
            <Button variant="destructive" size="sm" onClick={handleDelete}>Remove</Button>
          </div>
        ) : (
          <form onSubmit={handleSetup} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-2">
                <Label>Sender Name</Label>
                <Input aria-label="Sender Name" placeholder="Your Company" value={senderName} onChange={(e) => setSenderName(e.target.value)} disabled={isSubmitting} />
              </div>
              <div className="space-y-2">
                <Label>Sender Email</Label>
                <Input aria-label="Sender Email" type="email" placeholder="alerts@yourcompany.com" value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)} disabled={isSubmitting} />
              </div>
            </div>
            <Button type="submit" disabled={isSubmitting || !senderName.trim() || !senderEmail.trim()}>
              {isSubmitting ? 'Sending verification...' : 'Set Up Custom Sender'}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  )
}

function SsoCard() {
  const [entityId, setEntityId] = useState('')
  const [ssoUrl, setSsoUrl] = useState('')
  const [certificate, setCertificate] = useState('')
  const [enforce, setEnforce] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { data, refetch } = useQuery({
    queryKey: ['sso-config'],
    queryFn: () => apiV2.getSsoConfig(),
  })

  const config = data?.data

  async function handleConfigure(e: React.FormEvent) {
    e.preventDefault()
    if (!entityId.trim() || !ssoUrl.trim() || !certificate.trim()) return
    setIsSubmitting(true)
    try {
      await apiV2.configureSso({ entity_id: entityId.trim(), sso_url: ssoUrl.trim(), certificate: certificate.trim(), enforce })
      toast.success('SSO configured successfully!')
      refetch()
    } catch {
      toast.error('Failed to configure SSO. Business plan required.')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleDelete() {
    try {
      await apiV2.deleteSso()
      refetch()
      setEntityId('')
      setSsoUrl('')
      setCertificate('')
      setEnforce(false)
      toast.success('SSO configuration removed')
    } catch {
      toast.error('Failed to remove SSO')
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>SSO / SAML <span className="text-xs font-normal text-muted-foreground">(Business)</span></CardTitle>
        <CardDescription>Let your team sign in with your identity provider (Okta, Azure AD, etc.).</CardDescription>
      </CardHeader>
      <CardContent>
        {config ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-status-up" />
              <span className="text-sm font-medium">SSO configured</span>
            </div>
            <div className="grid gap-2 text-sm">
              <div><span className="text-muted-foreground">Entity ID:</span> {config.entity_id}</div>
              <div><span className="text-muted-foreground">SSO URL:</span> {config.sso_url}</div>
              <div><span className="text-muted-foreground">Enforcement:</span> {config.enforce ? 'Password login disabled' : 'Optional'}</div>
            </div>
            <Button variant="destructive" size="sm" onClick={handleDelete}>Remove SSO</Button>
          </div>
        ) : (
          <form onSubmit={handleConfigure} className="space-y-3">
            <div className="space-y-2">
              <Label>IdP Entity ID</Label>
              <Input aria-label="IdP Entity ID" placeholder="https://idp.example.com/saml/metadata" value={entityId} onChange={(e) => setEntityId(e.target.value)} disabled={isSubmitting} />
            </div>
            <div className="space-y-2">
              <Label>SSO URL</Label>
              <Input aria-label="SSO URL" placeholder="https://idp.example.com/saml/sso" value={ssoUrl} onChange={(e) => setSsoUrl(e.target.value)} disabled={isSubmitting} />
            </div>
            <div className="space-y-2">
              <Label>X.509 Certificate (PEM)</Label>
              <textarea
                aria-label="X.509 Certificate (PEM)"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                rows={4}
                placeholder="-----BEGIN CERTIFICATE-----&#10;...&#10;-----END CERTIFICATE-----"
                value={certificate}
                onChange={(e) => setCertificate(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={enforce} onChange={(e) => setEnforce(e.target.checked)} className="h-4 w-4 rounded border-input accent-primary" />
              Enforce SSO (disable password login for non-admin users)
            </label>
            <Button type="submit" disabled={isSubmitting || !entityId.trim() || !ssoUrl.trim() || !certificate.trim()}>
              {isSubmitting ? 'Configuring...' : 'Configure SSO'}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  )
}

function TwoFactorCard() {
  const [setupData, setSetupData] = useState<{ otpauth_uri: string; encoded_secret: string; secret: string } | null>(null)
  const [confirmCode, setConfirmCode] = useState('')
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null)
  const [disableCode, setDisableCode] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { data: status, refetch } = useQuery({
    queryKey: ['2fa-status'],
    queryFn: () => apiV2.get2faStatus(),
  })

  const is2faEnabled = status?.enabled ?? false

  async function handleSetup() {
    setIsSubmitting(true)
    try {
      const data = await apiV2.setup2fa()
      setSetupData(data)
    } catch {
      toast.error('Failed to start 2FA setup')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleConfirm(e: React.FormEvent) {
    e.preventDefault()
    if (!setupData || !confirmCode.trim()) return

    setIsSubmitting(true)
    try {
      const result = await apiV2.confirm2fa(confirmCode.trim(), setupData.secret)
      setBackupCodes(result.backup_codes)
      setSetupData(null)
      setConfirmCode('')
      refetch()
      toast.success('Two-factor authentication enabled!')
    } catch {
      toast.error('Invalid code. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleDisable(e: React.FormEvent) {
    e.preventDefault()
    if (!disableCode.trim()) return

    setIsSubmitting(true)
    try {
      await apiV2.disable2fa(disableCode.trim())
      setDisableCode('')
      refetch()
      toast.success('Two-factor authentication disabled')
    } catch {
      toast.error('Invalid code. Enter a valid TOTP or backup code.')
    } finally {
      setIsSubmitting(false)
    }
  }

  // Show backup codes after setup
  if (backupCodes) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Save Your Backup Codes</CardTitle>
          <CardDescription>
            Store these codes in a safe place. Each code can only be used once.
            You won't be able to see them again.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-2 rounded-lg bg-muted p-4 font-mono text-sm">
            {backupCodes.map((code, i) => (
              <div key={i} className="text-center">{code}</div>
            ))}
          </div>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => {
              navigator.clipboard.writeText(backupCodes.join('\n'))
              toast.success('Backup codes copied to clipboard')
            }}
          >
            Copy to Clipboard
          </Button>
          <Button className="w-full" onClick={() => setBackupCodes(null)}>
            I've saved my codes
          </Button>
        </CardContent>
      </Card>
    )
  }

  // Show setup form
  if (setupData) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Set Up Two-Factor Authentication</CardTitle>
          <CardDescription>
            Add this key to your authenticator app (Google Authenticator, Authy, 1Password, etc.).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* No QR image from another site: the setup URI holds the secret, and an image service
              would receive it in the request URL. The key is shown here and as an otpauth link. */}
          <div className="rounded-lg border border-border bg-muted p-4 text-center">
            <p className="text-sm text-muted-foreground">Enter this key in your app:</p>
            <p className="mt-1 select-all break-all font-mono text-base font-medium">{setupData.encoded_secret}</p>
            {setupData.otpauth_uri.startsWith('otpauth://') && (
              <a href={setupData.otpauth_uri} className="mt-3 inline-block text-sm text-primary underline">
                Open in your authenticator app
              </a>
            )}
          </div>
          <form onSubmit={handleConfirm} className="space-y-3">
            <div className="space-y-2">
              <Label htmlFor="confirm-2fa">Enter the 6-digit code from your app</Label>
              <Input
                id="confirm-2fa"
                type="text"
                inputMode="numeric"
                placeholder="000000"
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value)}
                disabled={isSubmitting}
                maxLength={6}
                autoFocus
                className="text-center text-xl tracking-widest"
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" className="flex-1" onClick={() => setSetupData(null)}>
                Cancel
              </Button>
              <Button type="submit" className="flex-1" disabled={isSubmitting || confirmCode.length < 6}>
                {isSubmitting ? 'Verifying...' : 'Enable 2FA'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Two-Factor Authentication</CardTitle>
        <CardDescription>
          Add an extra layer of security to your account using an authenticator app.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {is2faEnabled ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-status-up" />
              <span className="text-sm font-medium">Two-factor authentication is enabled</span>
            </div>
            <form onSubmit={handleDisable} className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="disable-2fa">Enter a code to disable 2FA</Label>
                <Input
                  id="disable-2fa"
                  type="text"
                  placeholder="TOTP or backup code"
                  value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value)}
                  disabled={isSubmitting}
                />
              </div>
              <Button type="submit" variant="destructive" disabled={isSubmitting || !disableCode.trim()}>
                {isSubmitting ? 'Disabling...' : 'Disable 2FA'}
              </Button>
            </form>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-muted-foreground" />
              <span className="text-sm text-muted-foreground">Two-factor authentication is not enabled</span>
            </div>
            <Button onClick={handleSetup} disabled={isSubmitting}>
              {isSubmitting ? 'Setting up...' : 'Set Up 2FA'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Integrations Tab
// ---------------------------------------------------------------------------

function IntegrationsTab() {
  const queryClient = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [newClientName, setNewClientName] = useState('')
  const [newClientRedirectUri, setNewClientRedirectUri] = useState('')
  const [createdClient, setCreatedClient] = useState<OAuthClientWithSecret | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['oauth-clients'],
    queryFn: () => apiV2.getOAuthClients(),
  })

  const createMutation = useMutation({
    mutationFn: () =>
      apiV2.createOAuthClient({
        name: newClientName,
        redirect_uris: [newClientRedirectUri],
      }),
    onSuccess: (data) => {
      setCreatedClient(data)
      setShowCreate(false)
      setNewClientName('')
      setNewClientRedirectUri('')
      queryClient.invalidateQueries({ queryKey: ['oauth-clients'] })
      toast.success('OAuth client created')
    },
    onError: () => toast.error('Failed to create client'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiV2.deleteOAuthClient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['oauth-clients'] })
      toast.success('Client deleted')
    },
    onError: () => toast.error('Failed to delete client'),
  })

  const clients = data?.clients ?? []

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>OAuth Clients</CardTitle>
              <CardDescription>
                Manage third-party integrations that can access your Uptrack data via MCP.
              </CardDescription>
            </div>
            <Button onClick={() => setShowCreate(true)}>Add OAuth Client</Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : clients.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No OAuth clients configured. Add one to connect Claude or other MCP-compatible tools.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Client ID</TableHead>
                  <TableHead>Redirect URI</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((client) => (
                  <TableRow key={client.id}>
                    <TableCell className="font-medium">{client.name}</TableCell>
                    <TableCell className="font-mono text-xs">{client.id}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {client.redirect_uris?.[0] || '—'}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(client.inserted_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => deleteMutation.mutate(client.id)}
                        disabled={deleteMutation.isPending}
                      >
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add OAuth Client</DialogTitle>
            <DialogDescription>
              Create an OAuth client for third-party integrations like Claude.ai.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="client-name">Name</Label>
              <Input
                id="client-name"
                placeholder="e.g. Claude.ai"
                value={newClientName}
                onChange={(e) => setNewClientName(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="redirect-uri">Redirect URI</Label>
              <Input
                id="redirect-uri"
                placeholder="https://claude.ai/oauth/callback"
                value={newClientRedirectUri}
                onChange={(e) => setNewClientRedirectUri(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={!newClientName || !newClientRedirectUri || createMutation.isPending}
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show secret dialog (one-time reveal) */}
      <Dialog open={!!createdClient} onOpenChange={() => setCreatedClient(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Client Created</DialogTitle>
            <DialogDescription>
              Save these credentials now — the client secret will not be shown again.
            </DialogDescription>
          </DialogHeader>
          {createdClient && (
            <div className="space-y-3">
              <div>
                <Label>Client ID</Label>
                <Input aria-label="Client ID" readOnly value={createdClient.client_id} className="font-mono text-xs" />
              </div>
              <div>
                <Label>Client Secret</Label>
                <Input aria-label="Client Secret" readOnly value={createdClient.client_secret} className="font-mono text-xs" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => {
              navigator.clipboard.writeText(
                `Client ID: ${createdClient?.client_id}\nClient Secret: ${createdClient?.client_secret}`
              )
              toast.success('Copied to clipboard')
            }}>
              Copy Credentials
            </Button>
            <Button variant="outline" onClick={() => setCreatedClient(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function KeyStatusBadge({ apiKey }: { apiKey: V2ApiKey }) {
  if (!apiKey.is_active) {
    return <Badge variant="destructive">Revoked</Badge>
  }
  if (apiKey.expires_at && new Date(apiKey.expires_at) < new Date()) {
    return <Badge variant="secondary">Expired</Badge>
  }
  return <Badge variant="default">Active</Badge>
}
